import { Injectable, inject } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { environment } from "../environment";
import { BehaviorSubject, Observable, timer } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";
import { of } from "rxjs";
import { type SentryServerConfig, DEFAULT_SENTRY_CONFIG } from "./sentry.types";

/**
 * Manages Sentry configuration fetched from the server
 * Handles periodic refresh and fallback to defaults
 */
@Injectable({ providedIn: "root" })
export class SentryConfigService {
	private http = inject(HttpClient);

	private configSubject = new BehaviorSubject<SentryServerConfig>(
		DEFAULT_SENTRY_CONFIG,
	);
	public config$ = this.configSubject.asObservable();

	private refreshIntervalMs = 60 * 60 * 1000; // 1 hour
	private isInitialized = false;

	/**
	 * Initialize and fetch Sentry config from server
	 * Called during app bootstrap
	 */
	async initialize(): Promise<SentryServerConfig> {
		if (this.isInitialized) {
			return this.configSubject.value;
		}

		try {
			const config = await this.fetchConfig();
			this.configSubject.next(config);
			this.isInitialized = true;
			this.startPeriodicRefresh();
			return config;
		} catch (error) {
			console.warn(
				"[Sentry] Failed to fetch config from server, using defaults",
				error,
			);
			this.configSubject.next(DEFAULT_SENTRY_CONFIG);
			this.isInitialized = true;
			this.startPeriodicRefresh();
			return DEFAULT_SENTRY_CONFIG;
		}
	}

	/**
	 * Fetch Sentry config from server
	 * Falls back to defaults if request fails
	 */
	private async fetchConfig(): Promise<SentryServerConfig> {
		try {
			const response = await fetch(
				`${environment.serverUrl}/api/sentry-config`,
				{
					signal: AbortSignal.timeout(5000), // 5 second timeout
				},
			);

			if (!response.ok) {
				throw new Error(`Sentry config fetch failed: ${response.status}`);
			}

			// Edge wraps response in { status: 'success', data: {...} }
			const json = (await response.json()) as {
				status?: string;
				data?: Partial<SentryServerConfig>;
			};
			const data = json.data ?? (json as Partial<SentryServerConfig>);
			return this.mergeWithDefaults(data);
		} catch (error) {
			console.warn("[Sentry] Config fetch error:", error);
			throw error;
		}
	}

	/**
	 * Merge server config with defaults
	 */
	private mergeWithDefaults(
		serverConfig: Partial<SentryServerConfig>,
	): SentryServerConfig {
		return {
			...DEFAULT_SENTRY_CONFIG,
			...serverConfig,
		};
	}

	/**
	 * Start periodic refresh of Sentry config
	 * Checks every hour (or custom interval)
	 */
	private startPeriodicRefresh(): void {
		if (!this.isInitialized) return;

		timer(this.refreshIntervalMs, this.refreshIntervalMs)
			.pipe(
				switchMap(() => this.refreshConfig()),
				catchError((error) => {
					console.warn("[Sentry] Periodic config refresh failed:", error);
					return of(null);
				}),
			)
			.subscribe();
	}

	/**
	 * Manually refresh Sentry config
	 */
	private async refreshConfig(): Promise<void> {
		try {
			const newConfig = await this.fetchConfig();
			const currentConfig = this.configSubject.value;

			// Only update if config changed
			if (JSON.stringify(currentConfig) !== JSON.stringify(newConfig)) {
				console.log("[Sentry] Config updated from server");
				this.configSubject.next(newConfig);
				this.updateSentryDynamically(newConfig);
			}
		} catch (error) {
			console.warn("[Sentry] Failed to refresh config:", error);
		}
	}

	/**
	 * Update Sentry client settings dynamically
	 * This applies new config without reinitializing Sentry
	 */
	private updateSentryDynamically(config: SentryServerConfig): void {
		// Dynamic Sentry updates will be handled in the component/page level
		// by subscribing to config$ and updating integration settings as needed
	}

	/**
	 * Get current config synchronously
	 */
	getCurrentConfig(): SentryServerConfig {
		return this.configSubject.value;
	}

	/**
	 * Set custom refresh interval (in milliseconds)
	 */
	setRefreshInterval(intervalMs: number): void {
		this.refreshIntervalMs = intervalMs;
	}
}
